
// GameComponent.js with fixed animations
import React, { useState, useEffect } from 'react';
import { parseEther } from 'ethers';
import web3Service from '../services/Web3Service';
import { COINFLIP_MIN_BET_AMOUNT, MIN_BET_AMOUNT } from '../config';

const GameComponent = () => {
  // Game states
  const [betAmount, setBetAmount] = useState(COINFLIP_MIN_BET_AMOUNT.toString());
  const [vrfFee, setVrfFee] = useState('0');
  const [totalAmount, setTotalAmount] = useState(MIN_BET_AMOUNT.toString());
  const [choice, setChoice] = useState(0); // 0 for heads, 1 for tails
  const [isFlipping, setIsFlipping] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [animating, setAnimating] = useState(false); // Added animation control state
  
  // Result states
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [bets, setBets] = useState([]);
  
  // Contract data
  const [houseBalance, setHouseBalance] = useState('0');
  const [maxBet, setMaxBet] = useState('0');
  const [isOwner, setIsOwner] = useState(false);
  
  // Owner controls
  const [liquidityAmount, setLiquidityAmount] = useState('0.1');
  const [withdrawAmount, setWithdrawAmount] = useState('0.1');
  
  // Debug state to show current selection
  const [debugInfo, setDebugInfo] = useState({});
  
  // Process a bet result to include the actual outcome
  const processBetResult = (event) => {
    // The choice value from the blockchain is a BigNumber and needs to be converted
    // The blockchain uses 0 for Heads and 1 for Tails
    const choiceValue = Number(event.returnValues.choice);
    const playerChoice = choiceValue === 0 ? 'Heads' : 'Tails';
    const won = event.returnValues.won;
    
    console.log('Raw bet event data:', {
      choice: event.returnValues.choice,
      choiceValue: choiceValue,
      playerChoice: playerChoice,
      won: won
    });
    
    // Determine the actual result based on player's choice and whether they won
    let actualResult;
    if (playerChoice === 'Heads') {
      // If player chose Heads, they win if coin landed on Heads, lose if it landed on Tails
      actualResult = won ? 'Heads' : 'Tails';
    } else {
      // If player chose Tails, they win if coin landed on Tails, lose if it landed on Heads
      actualResult = won ? 'Tails' : 'Heads';
    }
    
    return {
      requestId: event.returnValues.requestId,
      amount: web3Service.fromWei(event.returnValues.amount),
      playerChoice: playerChoice,
      result: actualResult,
      won: won,
      payout: web3Service.fromWei(event.returnValues.payout)
    };
  };
  
  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Get contract data
        const { balance, max, vrfFee: fee } = await web3Service.getContractData();
        setHouseBalance(balance);
        setMaxBet(max);
        setVrfFee(fee);
        updateTotalAmount(betAmount, fee);
        
        // Check if user has pending bet
        const pendingBet = await web3Service.hasPendingBet();
        setHasPending(pendingBet);
        
        // Check if user is owner
        const ownerStatus = await web3Service.checkIfOwner();
        setIsOwner(ownerStatus);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    
    if (web3Service.contract && web3Service.account) {
      loadInitialData();
    }
  }, [betAmount]);
  
  // Set up event listeners
  useEffect(() => {
    // Only set up event listeners if web3Service is properly initialized
    if (!web3Service.contract || !web3Service.account) {
      console.log('Web3Service not fully initialized, skipping event setup');
      return;
    }
    
    console.log('Setting up event listeners for GameComponent');
    
    // Set up BetPlaced event listener
    const handleBetPlaced = (event) => {
      console.log('🎲 BetPlaced event received:', event);
      
      // Log for debugging
      console.log('BetPlaced choice:', event.returnValues.choice);
      
      setRequestId(event.returnValues.requestId);
      setIsFlipping(true);
      setHasPending(true);
      // Clear any previous result when a new bet is placed
      setShowResult(false);
    };
    
    // Set up BetSettled event listener
    const handleBetSettled = (event) => {
      console.log('💰 BetSettled event received:', event);
      
      // Process the bet result 
      const result = processBetResult(event);
      
      // Update bet history without duplicates
      setBets(prev => {
        // Convert requestIds to strings for comparison
        const requestIdStr = result.requestId.toString();
        if (prev.some(bet => bet.requestId.toString() === requestIdStr)) {
          return prev;
        }
        return [result, ...prev];
      });
      
      // Set animating flag first
      setAnimating(true);
      
      // Show result animation
      handleBetResult(result);
      
      // IMPORTANT: Only reset game state after animation completes
      setTimeout(() => {
        // Reset game state after animation
        setIsFlipping(false);
        setHasPending(false);
        setRequestId(null);
        setAnimating(false);
        
        // Refresh contract data
        refreshContractData();
      }, 11000); // Set timeout to 11 seconds (10s display + 1s buffer)
    };
    
    // Subscribe to events - store the result to track if subscription was successful
    const subscriptionSuccess = web3Service.subscribeToEvents(handleBetPlaced, handleBetSettled);
    console.log('Event subscription result:', subscriptionSuccess);
    
    // Clean up on unmount or if component re-renders
    return () => {
      console.log('Cleaning up event subscriptions in GameComponent');
      web3Service.clearEventSubscriptions();
    };
  }, []);  // Empty dependency array to only run once
  
  
  // Periodically check pending bet status
  useEffect(() => {
    if (!hasPending || !web3Service.contract || !web3Service.account) return;
    
    const checkPending = async () => {
      try {
        console.log('🔍 Checking pending bet status...');
        const isPending = await web3Service.hasPendingBet();
        
        if (hasPending && !isPending) {
          // Bet was settled but we might have missed the event
          if (requestId) {
            // Look for the settled event in past events
            const ourEvent = await web3Service.getPastBetSettledEvents(requestId);
            
            if (ourEvent) {
              // Process the bet result
              const result = processBetResult(ourEvent);
              
              // Set animating flag to prevent state resets
              setAnimating(true);
              
              // Update bet history without duplicates
              setBets(prev => {
                const resultIdStr = result.requestId.toString();
                if (prev.some(bet => bet.requestId.toString() === resultIdStr)) {
                  return prev;
                }
                return [result, ...prev];
              });
              
              // Show result animation
              handleBetResult(result);
              
              // Reset states after animation completes
              setTimeout(() => {
                setIsFlipping(false);
                setHasPending(false);
                setRequestId(null);
                setAnimating(false);
                
                // Refresh contract data
                refreshContractData();
              }, 11000); // 11 seconds to ensure modal has closed
              
              return; // Exit early to prevent state changes
            }
          }
          
          // Only update state if not animating
          if (!animating) {
            setIsFlipping(false);
            setHasPending(false);
            setRequestId(null);
          }
          
          // Refresh contract data
          refreshContractData();
        } else if (isPending !== hasPending) {
          setHasPending(isPending);
        }
      } catch (error) {
        console.error('❌ Error checking pending bet:', error);
      }
    };
    
    // Check immediately and then set up interval
    checkPending();
    const interval = setInterval(checkPending, 3000);
    
    return () => clearInterval(interval);
  }, [hasPending, requestId, animating]);
  
  // Calculate total bet amount including VRF fee
  const updateTotalAmount = (bet, fee) => {
    try {
      // Use window.BigInt instead of BigInt for better compatibility
      const betBigInt = window.BigInt(bet);
      const feeBigInt = window.BigInt(fee);
      setTotalAmount((betBigInt + feeBigInt).toString());
    } catch (error) {
      console.error('Error updating total amount:', error);
      // Fallback to regular number addition
      setTotalAmount((Number(bet) + Number(fee)).toString());
    }
  };
  
  // Refresh contract data (balance and max bet)
  const refreshContractData = async () => {
    try {
      const { balance, max } = await web3Service.getContractData();
      setHouseBalance(balance);
      setMaxBet(max);
    } catch (error) {
      console.error('Error refreshing contract data:', error);
    }
  };
  
  // Handle bet result (win or loss)
  const handleBetResult = (result) => {
    console.log('🎯 handleBetResult called with:', result);
    setResultData(result);
    setShowResult(true);
    
    // Keep result visible for 10 seconds
    setTimeout(() => {
      console.log('🎯 Timeout fired - hiding result');
      setShowResult(false);
      // Don't reset other states here anymore!
    }, 10000);
  };
  
  // Handle bet amount input change
  const handleBetAmountChange = (e) => {
    if (animating) return; // Don't allow changes during animation
    
    try {
      const newAmount = parseEther(e.target.value).toString();
      setBetAmount(newAmount);
      updateTotalAmount(newAmount, vrfFee);
    } catch (error) {
      console.error('Invalid bet amount:', error);
    }
  };
  
  // Update the choice state with proper logging
  const handleChoiceChange = (newChoice) => {
    if (animating) return; // Don't allow changes during animation
    
    console.log(`Changing choice from ${choice} to ${newChoice}`);
    setChoice(newChoice);
  };
  
  // Place a new bet
  const placeBet = async () => {
    if (!web3Service.contract || !web3Service.account) {
      console.log('❌ Cannot place bet: contract or account not available');
      return;
    }
    
    // Don't allow placing a bet during animation
    if (animating) {
      alert('Please wait for the current animation to complete');
      return;
    }
    
    try {
      // Check if user already has a pending bet
      const isPending = await web3Service.hasPendingBet();
      if (isPending) {
        alert('You already have a pending bet');
        return;
      }
      
      // Log the choice being sent
      console.log('Placing bet with choice:', choice, choice === 0 ? 'Heads' : 'Tails');
      
      // Place the bet
      const result = await web3Service.placeBet(choice, betAmount, totalAmount);
      
      if (result.success && result.requestId) {
        console.log('Bet placed successfully with requestId:', result.requestId);
        
        // Manually update state in case event listener missed it
        setRequestId(result.requestId);
        setIsFlipping(true);
        setHasPending(true);
      }
    } catch (error) {
      console.error('❌ Error placing bet:', error);
      alert('Error placing bet: ' + error.message);
      
      // Reset state in case of error
      setIsFlipping(false);
    }
  };
  
  // Add liquidity (owner only)
  const addLiquidity = async () => {
    if (!web3Service.contract || !web3Service.account || !isOwner) return;
    
    // Don't allow during animation
    if (animating) {
      alert('Please wait for the current animation to complete');
      return;
    }
    
    try {
      const amountWei = parseEther(liquidityAmount).toString();
      const success = await web3Service.addLiquidity(amountWei);
      
      if (success) {
        alert('Liquidity added successfully');
        refreshContractData();
      }
    } catch (error) {
      console.error('Error adding liquidity:', error);
      alert('Error adding liquidity: ' + error.message);
    }
  };
  
  // Remove liquidity (owner only)
  const removeLiquidity = async () => {
    if (!web3Service.contract || !web3Service.account || !isOwner) return;
    
    // Don't allow during animation
    if (animating) {
      alert('Please wait for the current animation to complete');
      return;
    }
    
    try {
      const amountWei = parseEther(withdrawAmount).toString();
      const success = await web3Service.removeLiquidity(amountWei);
      
      if (success) {
        alert('Liquidity removed successfully');
        refreshContractData();
      }
    } catch (error) {
      console.error('Error removing liquidity:', error);
      alert('Error removing liquidity: ' + error.message);
    }
  };
  
  // Force check for pending bet status
  const checkBetStatus = async () => {
    if (!web3Service.contract || !web3Service.account) return;
    
    // Don't check while animating
    if (animating) {
      alert('Please wait for the current animation to complete');
      return;
    }
    
    try {
      const isPending = await web3Service.hasPendingBet();
      
      if (!isPending && hasPending) {
        if (!animating) {
          setHasPending(false);
          setIsFlipping(false);
        }
        refreshContractData();
        alert('Your bet has been settled!');
      } else {
        alert(isPending ? 'Your bet is still pending' : 'You have no pending bets');
      }
    } catch (error) {
      console.error('Error checking bet status:', error);
      alert('Error checking bet status');
    }
  };
  
  return (
    <div className="game-area">
      <div className="contract-info">
        <div>House Balance: {web3Service.fromWei(houseBalance)} BNB</div>
        <div>Max Bet: {web3Service.fromWei(maxBet)} BNB</div>
      </div>      
      
      <div className="coin-container">
        <div className={`coin ${isFlipping ? 'flipping' : ''} ${animating ? 'animating' : ''}`}>
          {!isFlipping && <div className="coin-face">{choice === 0 ? 'H' : 'T'}</div>}
        </div>
      </div>
      
      {showResult && resultData && (
        <div className={`result-message ${resultData.won ? 'win' : 'lose'}`}>
          <div className="result-title">
            {resultData.won ? '🎉 YOU WON! 🎉' : 'Better luck next time!'}
          </div>
          <div className="result-details">
            <div>You chose: {resultData.playerChoice}</div>
            <div>Coin landed on: {resultData.result}</div>
            {resultData.won 
              ? <div>Payout: +{resultData.payout} BNB!</div>
              : <div>Lost: -{resultData.amount} BNB</div>
            }
          </div>
        </div>
      )}
      
      {hasPending && !animating && (
        <div className="pending-bet">
          <h3>Bet Status</h3>
          <p>
            Your bet is pending. Please wait for the result.
          </p>
          <button className="check-button" onClick={checkBetStatus}>
            Check Status
          </button>
        </div>
      )}
      
      <div className="betting-controls">
        <div className="choice-selector">
          <label>Choose Side:</label>
          <div className="choice-buttons">
            <button 
              className={choice === 0 ? 'selected' : ''}
              onClick={() => handleChoiceChange(0)}
              disabled={hasPending || isFlipping || animating}
            >
              Heads
            </button>
            <button 
              className={choice === 1 ? 'selected' : ''}
              onClick={() => handleChoiceChange(1)}
              disabled={hasPending || isFlipping || animating}
            >
              Tails
            </button>
          </div>
        </div>
        
        <div className="bet-amount">
          <label>Bet Amount (BNB):</label>
          <input 
            type="number" 
            min="0.015"
            step="0.001"
            value={web3Service.web3 ? web3Service.fromWei(betAmount) : '0.015'}
            onChange={handleBetAmountChange}
            disabled={hasPending || isFlipping || animating}
          />
          <div className="min-bet">Minimum bet: 0.015 BNB</div>
        </div>
        
        <div className="bet-summary">
          <div>Fee: {web3Service.fromWei(vrfFee)} BNB</div>
          <div className="total-cost">Total Cost: {web3Service.fromWei(totalAmount)} BNB</div>
        </div>
        
        <button 
          className="place-bet-button"
          onClick={placeBet}
          disabled={!web3Service.account || hasPending || isFlipping || animating}
        >
          {hasPending ? 'Bet Pending...' : isFlipping ? 'Flipping...' : animating ? 'Animation...' : `Place Bet on ${choice === 0 ? 'Heads' : 'Tails'}`}
        </button>
      </div>
      
      {isOwner && (
        <div className="owner-controls">
          <h2>Owner Controls</h2>
          
          <div className="owner-actions">
            <div className="liquidity-control">
              <label>Add Liquidity (BNB):</label>
              <div className="input-with-button">
                <input 
                  type="number" 
                  min="0.01"
                  step="0.01"
                  value={liquidityAmount}
                  onChange={(e) => setLiquidityAmount(e.target.value)}
                  disabled={animating}
                />
                <button onClick={addLiquidity} disabled={animating}>+</button>
              </div>
            </div>
            
            <div className="liquidity-control">
              <label>Remove Liquidity (BNB):</label>
              <div className="input-with-button">
                <input 
                  type="number" 
                  min="0.01"
                  step="0.01"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  disabled={animating}
                />
                <button className="remove-button" onClick={removeLiquidity} disabled={animating}>-</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {bets.length > 0 && (
        <div className="bet-history">
          <h2>Bet History</h2>
          <div className="history-list">
            {bets.map((bet) => (
              <div 
                key={bet.requestId.toString()} 
                className={`history-item ${bet.won ? 'win' : 'lose'}`}
              >
                <div>Bet: {bet.amount} BNB on {bet.playerChoice}</div>
                <div>Result: Coin landed on {bet.result} {bet.won ? `(Won ${bet.payout} BNB)` : '(Lost)'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Win Animation Modal */}
      {showResult && resultData && resultData.won && (
        <div className="result-modal win-modal" onClick={() => setShowResult(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>YOU WON!</h2>
            <div className="coin-result">Coin landed on {resultData.result}</div>
            <div className="win-amount">{resultData.payout} BNB</div>
            <div className="confetti-container">
              {Array.from({ length: 50 }).map((_, i) => (
                <div 
                  key={i} 
                  className="confetti" 
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    backgroundColor: ['#FCD34D', '#FBBF24', '#F59E0B', '#D97706'][Math.floor(Math.random() * 4)]
                  }}
                ></div>
              ))}
            </div>
            <button className="modal-button" onClick={() => setShowResult(false)}>
              Awesome!
            </button>
          </div>
        </div>
      )}
      
      {/* Loss Animation Modal */}
      {showResult && resultData && !resultData.won && (
        <div className="result-modal lose-modal" onClick={() => setShowResult(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>UNLUCKY!</h2>
            <div className="coin-result">Coin landed on {resultData.result}</div>
            <div className="lose-amount">-{resultData.amount} BNB</div>
            <div className="rain-container">
              {Array.from({ length: 30 }).map((_, i) => (
                <div 
                  key={i} 
                  className="raindrop" 
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${0.5 + Math.random() * 1}s`
                  }}
                ></div>
              ))}
            </div>
            <button className="modal-button" onClick={() => setShowResult(false)}>
              Awww!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameComponent;